/**
 * SharedLandingNavbar Component
 *
 * Reusable navigation bar for all visitor-facing (unauthenticated) pages.
 * Extracted from the inline AppBar in LandingPage.tsx.
 *
 * - Left: NavBarLogo
 * - Center: route link buttons (hidden below 768px)
 * - Right: Sign In (outlined) + Sign Up (contained) buttons (hidden below 768px)
 * - Mobile (< 768px): hamburger icon opens a Drawer with all links + auth buttons
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 8.1, 8.2
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Box,
    Button,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { NavBarLogo } from './NavBarLogo';

const NAV_LINKS = [
    { label: 'Home', path: '/' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Market', path: '/market' },
    { label: 'Forum', path: '/forum' },
    { label: 'Support', path: '/support' },
] as const;

export function SharedLandingNavbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Build the post-login redirect param. We default to ON: the user
    // almost always wants to return to where they were when they clicked
    // Sign In. The only exceptions are:
    //   - '/' — the marketing landing is not a useful return destination
    //     once the user is authenticated (LandingRoute would just bounce
    //     them again).
    //   - '/login' — already on the login page; nothing to return to.
    const currentPath = location.pathname;
    const SKIP_REDIRECT = currentPath === '/' || currentPath === '/login';
    const fullPath = `${currentPath}${location.search}${location.hash}`;
    const redirectParam = SKIP_REDIRECT
        ? ''
        : `?redirect=${encodeURIComponent(fullPath)}`;
    const loginUrl = `/login${redirectParam}`;
    const signUpUrl = `/login?mode=signup${SKIP_REDIRECT ? '' : `&redirect=${encodeURIComponent(fullPath)}`}`;

    const handleDrawerToggle = () => {
        setDrawerOpen((prev) => !prev);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const handleNavClick = (path: string) => {
        navigate(path);
        handleDrawerClose();
    };

    return (
        <AppBar
            component="nav"
            position="fixed"
            elevation={0}
            sx={{
                backgroundColor: 'transparent',
                color: '#fff',
                backdropFilter: 'blur(8px)',
            }}
        >
            <Toolbar
                sx={{
                    maxWidth: 1200,
                    width: '100%',
                    mx: 'auto',
                    minHeight: { xs: '56px', sm: '64px' },
                    position: 'relative',
                }}
            >
                {/* Logo — left aligned */}
                <NavBarLogo />

                {/* Navigation links — centered via absolute positioning, hidden below 768px */}
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        gap: 1.5,
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                    }}
                >
                    {NAV_LINKS.map((item) => (
                        <Button
                            key={item.label}
                            onClick={() => navigate(item.path)}
                            sx={{
                                fontWeight: 500,
                                textTransform: 'none',
                                fontSize: '0.9rem',
                                color: 'inherit',
                                borderRadius: '20px',
                                px: 2,
                                py: 0.5,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        >
                            {item.label}
                        </Button>
                    ))}
                </Box>

                {/* Spacer */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Desktop: Sign In + Sign Up buttons — hidden below 768px */}
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        component={Link}
                        to={loginUrl}
                        sx={{
                            borderColor: 'divider',
                            color: 'inherit',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: 'inherit',
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        Sign In
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        component={Link}
                        to={signUpUrl}
                        sx={{
                            textTransform: 'none',
                        }}
                    >
                        Sign Up
                    </Button>
                </Box>

                {/* Mobile: hamburger icon — visible below 768px */}
                <IconButton
                    color="inherit"
                    aria-label="Open navigation menu"
                    aria-expanded={drawerOpen}
                    onClick={handleDrawerToggle}
                    sx={{ display: { xs: 'flex', md: 'none' } }}
                >
                    <MenuIcon />
                </IconButton>

                {/* Mobile drawer */}
                <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                >
                    <Box sx={{ width: 250 }} role="presentation">
                        <List>
                            {NAV_LINKS.map((item) => (
                                <ListItem key={item.label} disablePadding>
                                    <ListItemButton
                                        onClick={() => handleNavClick(item.path)}
                                    >
                                        <ListItemText primary={item.label} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => handleNavClick(loginUrl)}
                                >
                                    <ListItemText primary="Sign In" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => handleNavClick(signUpUrl)}
                                >
                                    <ListItemText primary="Sign Up" />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Box>
                </Drawer>
            </Toolbar>
        </AppBar>
    );
}
